import classNames from "classnames";
import { Modal } from "react-bootstrap";
import { Controller } from "react-hook-form";
import { Button } from "../../../components";
import { CurrencyInput, DateInput, FormInput, FormLabel, FormSelect, FormTextArea } from "../../../components/forms";
import { ClientProjectStatusSelect, GenericUserSelect } from "../../../components/forms/selects";
import { AdviceTypes } from "../../../components/forms/selects/AdviceTypesSelect";
import useCreateSetProject from "../hooks/useCreateSetProject";
import ThemedModal from "../../../components/utilities/ThemedModal";

const CreateSetProjectModal = ({ ...modalProps }) => {
    const { onHide } = modalProps;

    const {
        selectedSetName,
        selectedSetCount,
        driverId,
        messageSubject,
        messageDriver,
        messageAdviser,
        control,
        setValue,
        errors,
        onSubmit,
        onCancel,
    } = useCreateSetProject({ onHide });

    return <Modal size="lg" centered onHide={onCancel} backdrop="static" {...modalProps}>
        <ThemedModal.Header>
            <Modal.Title>
                Create a Project
            </Modal.Title>
        </ThemedModal.Header>
        <form onSubmit={onSubmit}>
            <Modal.Body>
                <p>
                    Would you like to create a project for '{selectedSetName}' ({selectedSetCount} accounts)?
                </p>
                <div className="row gy-2">
                    <div className="col-12">
                        <Controller
                            control={control}
                            name="description"
                            rules={{ required: "Project description is required" }}
                            render={({ field: { onChange, value, ...rest } }) => (
                                <FormInput
                                    {...rest}
                                    label="Description"
                                    value={value}
                                    onChange={(e) => setValue('description', e.target.value)}
                                    errorMessage={errors.projectDescription?.message}
                                    errorAllowRetry={false}
                                    disableAnimations
                                />
                            )}
                        />
                    </div>
                    <div className="col-12">
                        <Controller
                            control={control}
                            name="comment"
                            render={({ field: { onChange, value, ...rest } }) => (
                                <FormInput
                                    {...rest}
                                    label="Comment"
                                    value={value}
                                    onChange={(e) => setValue('comment', e.target.value)}
                                    disableAnimations
                                />
                            )}
                        />
                    </div>
                    <div className="col-12 col-lg-3">
                        <Controller
                            control={control}
                            name="adviceType"
                            rules={{ required: "Advice type is required" }}
                            render={({ field: { onChange, value, ...rest } }) => (
                                <FormSelect
                                    {...rest}
                                    label="Advice Type"
                                    defaultValue={value}
                                    onChange={(selection) => setValue('adviceType', selection.value)}
                                    options={AdviceTypes}
                                    errorMessage={errors.adviceType?.message}
                                    errorAllowRetry={false}
                                    disableAnimations
                                />
                            )}
                        />
                    </div>
                    <div className="col-4 col-lg-3">
                        <Controller
                            control={control}
                            name="status"
                            rules={{ required: "Status is required" }}
                            render={({ field: { onChange, value, ...rest } }) => (
                                <ClientProjectStatusSelect
                                    label="Status"
                                    defaultValue={value}
                                    onChange={(selection) => setValue('status', selection.value)}
                                    errorMessage={errors.status?.message}
                                    errorAllowRetry={false}
                                    disableAnimations
                                    {...rest}
                                />
                            )}
                        />
                    </div>
                    <div className="col-4 col-lg-3">
                        <Controller
                            control={control}
                            name="revenue"
                            render={({ field: { onChange, value, ...rest } }) => (
                                <CurrencyInput
                                    {...rest}
                                    label="Revenue"
                                    value={value}
                                    onChange={(_, { floatValue }) => setValue('revenue', floatValue)}
                                    disableAnimations
                                />
                            )}
                        />
                    </div>
                    <div className="col-4 col-lg-3">
                        <Controller
                            control={control}
                            name="targetDate"
                            rules={{
                                required: "Target date is required",
                                validate: (value) => new Date(value) > new Date() || "Target date must be in the future"
                            }}
                            render={({ field: { onChange, onBlur, value, ...rest } }) => (
                                <DateInput
                                    {...rest}
                                    label="Target Date"
                                    value={value || null}
                                    minDate={new Date()}
                                    onChange={(newValue) => setValue('targetDate', newValue)}
                                    onBlur={(newValue) => setValue('targetDate', newValue)}
                                    errorMessage={errors.targetDate?.message}
                                    errorAllowRetry={false}
                                    disableAnimations
                                />
                            )}
                        />
                    </div>
                    <div className="col-12">
                        <Controller
                            control={control}
                            name="driverId"
                            render={({ field: { onChange, value, ...rest } }) => (
                                <GenericUserSelect
                                    {...rest}
                                    label="Driver"
                                    defaultValue={value}
                                    onChange={(selection) => setValue('driverId', selection.value)}
                                    isClearable
                                    disableAnimations
                                />)}
                        />
                    </div>
                    <div className={classNames("col-12 pt-2", { "d-none": !messageDriver && !messageAdviser })}>
                        <FormLabel>Subject</FormLabel>
                        <p className="m-0">{messageSubject}</p>
                    </div>
                    <div className="col-12">
                        <FormSelect
                            label="Message Recipients"
                            defaultValue={(messageDriver ? [0] : []).concat(messageAdviser ? [1] : [])}
                            onChange={(selections) => {
                                setValue('messageDriver', selections.map(sel => sel.value).includes(0));
                                setValue('messageAdviser', selections.map(sel => sel.value).includes(1));
                            }}
                            options={[
                                { label: "Driver", value: 0, name: "messageDriver", isDisabled: driverId == null },
                                { label: "Adviser", value: 1, name: "messageAdviser" }
                            ]}
                            // Show error border if the message content has an error (so is empty) and the message recipients are not empty
                            errorMessage={errors.messageContent != null && (messageDriver || messageAdviser)
                                ? " "
                                : null}
                            errorAllowRetry={false}
                            isMulti
                            disableAnimations
                        />
                    </div>
                    <div className={classNames("col-12", { "d-none": !messageDriver && !messageAdviser })}>
                        <Controller
                            control={control}
                            name="messageContent"
                            rules={{
                                validate: (value) => (!messageDriver && !messageAdviser)
                                    || value?.length > 0
                                    || "Please enter a message or remove all recipients."
                            }}
                            render={({ field: { onChange, value, ref, ...rest } }) => (
                                <FormTextArea
                                    {...rest}
                                    placeholder="Message..."
                                    value={value}
                                    minRows={3}
                                    maxRows={6}
                                    onChange={(e) => setValue('messageContent', e.target.value)}
                                    errorMessage={errors.messageContent?.message}
                                    errorAllowRetry={false}
                                    disableAnimations
                                />
                            )}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success" type="submit">Create</Button>
                <Button variant="light" onClick={onCancel}>Cancel</Button>
            </Modal.Footer>
        </form>
    </Modal>
}

export default CreateSetProjectModal